import { ChevronDown, LogOut, Plus, Settings } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTopUp } from '@/features/ai/credits/CreditsDialogContext'
import { formatCreditsWithWord } from '@/features/ai/lib/money'
import { useCreditsDisplay } from './useCreditsDisplay'
import { AuthorBadges } from '@/components/common/AuthorBadges'
import { LanguageMenu } from './LanguageMenu'
import { CreditsPlate } from './CreditsPlate'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import {
  useSidebar,
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SidebarMenuButton,
} from '@teach/ui'

interface Props {
  userName: string
  userAvatar: string | null
  initials: string
  isAuthor?: boolean
  isVerified?: boolean
  onSignOut: () => void
}

export const UserProfileDropdown = ({
  userName,
  userAvatar,
  initials,
  isAuthor = false,
  isVerified = false,
  onSignOut,
}: Props) => {
  const navigate = useNavigate()
  const { open: openTopUp } = useTopUp()
  // На телефоне и планшете меню — шторка поверх: покупка открывается своим окном,
  // и шторка под ним оставалась открытой. Переходы закрывает `AppSidebar`.
  const { isMobile, setOpenMobile } = useSidebar()
  const credits = useCreditsDisplay()
  const m = useMessages(messages)
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton size="lg" className="group/user data-[state=open]:bg-foreground/10">
          <Avatar className="size-8 rounded-lg">
            {userAvatar ? <AvatarImage src={userAvatar} alt={userName} /> : null}
            <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
          </Avatar>
          <div className="grid h-8 flex-1 grid-rows-2 text-left text-sm leading-4">
            <span className="flex items-center gap-2">
              <AuthorBadges beta={isAuthor} verified={isVerified} />
              <span className="-ml-px truncate font-medium">{userName}</span>
            </span>
            {/* Сдвиг на полтора пикселя: низ цифр (базовая линия) встаёт вровень с
                низом аватарки — у бокса строки под цифрами ещё запас под хвосты букв. */}
            <span className="translate-y-[1.5px] self-end truncate text-xs leading-none text-muted-foreground">
              {credits.unavailable ? (
                '—'
              ) : credits.balance ? (
                formatCreditsWithWord(credits.shownBalance)
              ) : (
                <span className="inline-block h-3 w-16 animate-pulse rounded bg-foreground/10" />
              )}
            </span>
          </div>
          <ChevronDown className="ml-auto mr-3 size-4 transition-transform group-data-[state=open]/user:rotate-180" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
        align="start"
        side="bottom"
        sideOffset={4}
      >
        {/* Карточка кредитов: баланс и покупка — два соседних пункта меню, а не
            кнопка внутри пункта (вложенный интерактивный элемент ломает
            клавиатурный обход Radix). Пилюля справа — просто оформление. */}
        <div className="mb-1 rounded-xl bg-foreground/5 p-0.5">
          <DropdownMenuItem
            className="rounded-lg p-0"
            onClick={() => navigate('/settings/balance')}
          >
            <CreditsPlate />
          </DropdownMenuItem>
          <DropdownMenuSeparator className="mx-2 my-0.5" />
          <DropdownMenuItem
            className="group/buy gap-2 whitespace-nowrap rounded-lg px-2 py-2"
            onClick={() => {
              if (isMobile) setOpenMobile(false)
              openTopUp(null, 'packages')
            }}
          >
            <Plus
              className="size-4 shrink-0 text-brand transition-transform group-hover/buy:scale-125 group-focus/buy:scale-125"
              strokeWidth={3}
            />
            {m.notEnough}
            <span className="ml-auto shrink-0 rounded-full bg-brand px-3 py-1 transition-[background-color,transform] group-hover/buy:scale-105 group-hover/buy:bg-brand-hover group-focus/buy:scale-105 group-focus/buy:bg-brand-hover text-xs font-semibold text-brand-foreground">
              {m.buy}
            </span>
          </DropdownMenuItem>
        </div>
        <DropdownMenuItem onClick={() => navigate('/settings')}>
          <Settings className="mr-2 size-4" />
          {m.settings}
        </DropdownMenuItem>
        <LanguageMenu />
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onSignOut}>
          <LogOut className="mr-2 size-4" />
          {m.signOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
